import React, { useRef, useState } from "react";
import { Sparkles, Loader2, UploadCloud } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const COLOR = { "Concluído": "bg-emerald-100 text-emerald-700", "Em Andamento": "bg-amber-100 text-amber-700", Falha: "bg-rose-100 text-rose-700" };

export function IsoAiImport({ onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const inputRef = useRef(null);

  const run = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) { toast.error("Selecione um arquivo .zip"); return; }
    setBusy(true); setJob(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/iso14001/ai-zip", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      let st = { total: data.total, processed: 0, results: [] };
      while (st.status !== "done") {
        await new Promise((r) => setTimeout(r, 2500));
        st = (await api.get(`/ai-zip-status/${data.job_id}`)).data;
        setJob(st);
      }
      toast.success(`${st.matched} documento(s) encaixados no checklist${st.unmatched ? ` · ${st.unmatched} em Documentos Importados` : ""}`);
      onDone && onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na importação"); }
    setBusy(false);
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { setJob(null); setOpen(true); }} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="iso-ai-zip-btn">
        <Sparkles className="mr-1.5 h-4 w-4" /> Importar ZIP (IA)
      </Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]" data-testid="iso-ai-zip-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Importar evidências ISO 14001 com IA</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Envie um <b>.zip</b> com os documentos. A IA lê cada arquivo, escolhe o item do checklist em que ele se encaixa, anexa e muda o status para <b>Concluído</b> ou <b>Em Andamento</b>. O anexo anterior do item é guardado no GED.</p>
          {!job && (
            <div onClick={() => !busy && inputRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); run(e.dataTransfer.files?.[0]); }}
              className="mt-2 flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#E2E8F0] bg-slate-50/50 p-8 text-center hover:border-emerald-500" data-testid="iso-ai-zip-dropzone">
              {busy ? <Loader2 className="h-7 w-7 animate-spin text-emerald-600" /> : <UploadCloud className="h-7 w-7 text-emerald-600" />}
              <p className="text-sm font-medium text-slate-600">{busy ? "Enviando..." : "Arraste o .zip ou clique para selecionar"}</p>
              <p className="text-xs text-slate-400">PDF e imagens são lidos pela IA; outros formatos vão para Documentos Importados</p>
              <input ref={inputRef} type="file" accept=".zip" className="hidden" onChange={(e) => run(e.target.files?.[0])} />
            </div>
          )}
          {job && (
            <div className="space-y-2" data-testid="iso-ai-zip-results">
              <p className="flex items-center gap-2 text-sm font-semibold text-[#0B2545]">
                {job.status !== "done" && <Loader2 className="h-4 w-4 animate-spin" />} {job.processed}/{job.total} documento(s) analisado(s)
              </p>
              <div className="max-h-[360px] space-y-1.5 overflow-y-auto pr-1">
                {(job.results || []).map((r, i) => (
                  <div key={i} className="rounded-lg border border-slate-100 bg-slate-50/60 p-2 text-xs" data-testid={`iso-ai-result-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="flex-1 truncate font-medium text-slate-700" title={r.file}>{r.file}</span>
                      {r.status && <span className={`rounded-md px-2 py-0.5 font-semibold ${COLOR[r.status] || "bg-slate-100"}`}>{r.status}</span>}
                    </div>
                    <p className="mt-0.5 text-slate-500">→ {r.item || "—"}{r.reason ? ` · ${r.reason}` : ""}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {job?.status === "done" && <DialogFooter><Button onClick={() => setOpen(false)} className="bg-[#0B2545]" data-testid="iso-ai-zip-close">Fechar</Button></DialogFooter>}
        </DialogContent>
      </Dialog>
    </>
  );
}
